<template>
  <div class="license-table">
    <!-- Filters & Sort -->
    <div v-if="licenses.length" class="license-table__filters">
      <div class="filter-block">
        <div class="filter-block__label">
          {{ $t('Start date') }}
        </div>

        <div class="date-range">
          <label class="filter-field">
            <span>{{ $t('From') }}</span>
            <input
                v-model="filters.startDateFrom"
                type="date"
                class="filter-input"
            >
          </label>

          <span class="date-range__separator">–</span>

          <label class="filter-field">
            <span>{{ $t('To') }}</span>
            <input
                v-model="filters.startDateTo"
                type="date"
                class="filter-input"
            >
          </label>
        </div>
      </div>

      <div class="filter-block">
        <div class="filter-block__label">
          {{ $t('End date') }}
        </div>

        <div class="date-range">
          <label class="filter-field">
            <span>{{ $t('From') }}</span>
            <input
                v-model="filters.endDateFrom"
                type="date"
                class="filter-input"
            >
          </label>

          <span class="date-range__separator">–</span>

          <label class="filter-field">
            <span>{{ $t('To') }}</span>
            <input
                v-model="filters.endDateTo"
                type="date"
                class="filter-input"
            >
          </label>
        </div>
      </div>

      <div class="filter-block filter-block--small">
        <label class="filter-field">
      <span class="filter-block__label">
        {{ $t('License type') }}
      </span>

          <select v-model="filters.licenseType" class="filter-input">
            <option value="">{{ $t('All types') }}</option>

            <option
                v-for="type in availableTypes"
                :key="type"
                :value="type"
            >
              {{ type }}
            </option>
          </select>
        </label>
      </div>

      <div class="filter-block filter-block--sort">
        <div class="filter-block__label">
          {{ $t('Sort by') }}
        </div>

        <div class="sort-control">
          <select v-model="sortField" class="filter-input">
            <option value="updatedAt">{{ $t('Updated at') }}</option>
            <option value="createdAt">{{ $t('Created at') }}</option>
            <option value="startDate">{{ $t('Start date') }}</option>
            <option value="expirationDate">{{ $t('End date') }}</option>
          </select>

          <button
              type="button"
              class="sort-direction"
              :title="sortDirection === 'asc'
            ? $t('Ascending')
            : $t('Descending')"
              :aria-label="sortDirection === 'asc'
            ? $t('Sort ascending')
            : $t('Sort descending')"
              @click="toggleSortDirection"
          >
        <span aria-hidden="true">
          {{ sortDirection === 'asc' ? '↑' : '↓' }}
        </span>
          </button>
        </div>
      </div>

      <button
          type="button"
          class="filter-reset"
          @click="resetFilters"
      >
        {{ $t('Reset') }}
      </button>
    </div>

    <div v-if="licenses.length && !filteredAndSortedLicenses.length" class="license-table__empty">
      {{ $t('No licenses match the current filters') }}
    </div>

    <div v-if="!licenses.length" class="license-table__empty">
      {{ $t('No licenses found') }}
    </div>

    <div
        v-if="filteredAndSortedLicenses.length"
        class="license-table__toolbar"
    >
      <label class="select-all-label">
        <input
            type="checkbox"
            class="pretty-checkbox"
            :checked="allSelected"
            :indeterminate.prop="someSelected && !allSelected"
            @change="toggleAll"
        >
        <span class="pretty-checkbox__box" aria-hidden="true"></span>

        <span>
          {{ allSelected ? $t('Deselect all') : $t('Select all') }}

          <span
              v-if="selectedIds.length"
              class="selection-count"
          >
            ({{ selectedIds.length }} {{ $t('selected') }})
          </span>
        </span>
      </label>

      <span class="filter-count">
        {{ filteredAndSortedLicenses.length }} / {{ licenses.length }} {{ $t('licenses') }}
      </span>
    </div>

    <div
        v-for="license in filteredAndSortedLicenses"
        :key="license.id"
        class="license-row"
        :class="{ 'license-row--selected': selectedIds.includes(license.id) }"
    >
      <label class="license-row__select">
        <input
            type="checkbox"
            class="pretty-checkbox"
            :checked="selectedIds.includes(license.id)"
            @change="toggleSelect(license.id)"
        >
        <span class="pretty-checkbox__box" aria-hidden="true"></span>
      </label>

      <div class="license-row__main">
        <div class="license-row__header">
          <div>
            <div class="license-row__title">
              {{ license.name || $t('Unnamed license') }}
            </div>

            <div class="license-row__subtitle">
              {{ getOrganizationName(license.organizationId) }}
            </div>
          </div>

          <div
              class="license-status"
              :class="getStateClass(license.state)"
          >
            {{ license.state || $t('unknown') }}
          </div>
        </div>

        <div class="license-row__grid">
          <div class="license-field">
            <div class="license-field__label">
              {{ $t('startsAt') }}
            </div>

            <div
                class="license-field__value"
                :class="getDateClass(license.startDate, true)"
            >
              {{ formatDate(license.startDate) }}
            </div>
          </div>

          <div class="license-field">
            <div class="license-field__label">
              {{ $t('expiresAt') }}
            </div>

            <div
                class="license-field__value"
                :class="getDateClass(license.expirationDate)"
            >
              {{ formatDate(license.expirationDate) }}
            </div>
          </div>

          <div class="license-field">
            <div class="license-field__label">
              {{ $t('licenseType') }}
            </div>

            <div class="license-field__value">
              <span class="license-type">
                {{ license.type || '-' }}
              </span>
            </div>
          </div>

          <!--<div class="license-field">
            <div class="license-field__label">
              {{ $t('price') }}
            </div>

            <div class="license-field__value">
              {{ formatPrice(license.price) }}
            </div>
          </div>-->

          <div class="license-field">
            <div class="license-field__label">
              {{ $t('userName') }}
            </div>

            <div class="license-field__value">
              {{ getUserName(license.userId) }}
            </div>
          </div>

          <div class="license-field">
            <div class="license-field__label">
              {{ $t('organization') }}
            </div>

            <div class="license-field__value">
              {{ getOrganizationName(license.organizationId) }}
            </div>
          </div>
        </div>
      </div>

      <div class="license-row__actions">
        <button
            type="button"
            class="license-action license-action--edit"
            @click="$emit('edit', license)"
        >
          {{ $t('Modify') }}
        </button>

        <button
            type="button"
            class="license-action license-action--delete"
            @click="$emit('delete', license.id)"
        >
          {{ $t('Delete') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LicenseTable',

  props: {
    licenses: {
      type: Array,
      default: () => []
    },

    users: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      selectedIds: [],
      filters: {
        startDateFrom: '',
        startDateTo: '',
        endDateFrom: '',
        endDateTo: '',
        licenseType: ''
      },
      sortField: 'updatedAt',
      sortDirection: 'desc'
    };
  },

  computed: {
    availableTypes() {
      const types = this.licenses
          .map(l => l.type)
          .filter(Boolean);
      return [...new Set(types)].sort();
    },

    filteredAndSortedLicenses() {
      let result = this.licenses.filter(license => {
        if (this.filters.startDateFrom) {
          const from = new Date(this.filters.startDateFrom);
          const start = new Date(license.startDate);
          if (!license.startDate || start < from) return false;
        }

        if (this.filters.startDateTo) {
          const to = new Date(this.filters.startDateTo);
          const start = new Date(license.startDate);
          if (!license.startDate || start > to) return false;
        }

        if (this.filters.endDateFrom) {
          const from = new Date(this.filters.endDateFrom);
          const end = new Date(license.expirationDate);
          if (!license.expirationDate || end < from) return false;
        }

        if (this.filters.endDateTo) {
          const to = new Date(this.filters.endDateTo);
          const end = new Date(license.expirationDate);
          if (!license.expirationDate || end > to) return false;
        }

        if (this.filters.licenseType) {
          if (license.type !== this.filters.licenseType) return false;
        }

        return true;
      });

      const field = this.sortField;
      const dir = this.sortDirection === 'asc' ? 1 : -1;

      result = [...result].sort((a, b) => {
        const valA = a[field] ? new Date(a[field]).getTime() : 0;
        const valB = b[field] ? new Date(b[field]).getTime() : 0;
        return (valA - valB) * dir;
      });

      return result;
    },

    allSelected() {
      return this.filteredAndSortedLicenses.length > 0 &&
          this.selectedIds.length === this.filteredAndSortedLicenses.length;
    },

    someSelected() {
      return this.selectedIds.length > 0;
    }
  },

  watch: {
    licenses() {
      this.selectedIds = [];
      this.$emit('selection-change', []);
    }
  },

  methods: {
    toggleSortDirection() {
      this.sortDirection = this.sortDirection === 'asc'
          ? 'desc'
          : 'asc';
    },
    resetFilters() {
      this.filters = {
        startDateFrom: '',
        startDateTo: '',
        endDateFrom: '',
        endDateTo: '',
        licenseType: ''
      };
      this.sortField = 'updatedAt';
      this.sortDirection = 'desc';
    },

    toggleSelect(id) {
      const idx = this.selectedIds.indexOf(id);

      if (idx === -1) {
        this.selectedIds.push(id);
      } else {
        this.selectedIds.splice(idx, 1);
      }

      this.$emit('selection-change', [...this.selectedIds]);
    },

    toggleAll() {
      if (this.allSelected) {
        this.selectedIds = [];
      } else {
        this.selectedIds = this.filteredAndSortedLicenses.map(license => license.id);
      }

      this.$emit('selection-change', [...this.selectedIds]);
    },

    clearSelection() {
      this.selectedIds = [];
      this.$emit('selection-change', []);
    },

    getUserName(userId) {
      if (!userId) {
        return 'N/A';
      }

      const user = this.users.find(user => user.id === userId);
      return user ? user.name : 'N/A';
    },

    getOrganizationName(organizationId) {
      const organization = this.$store.getters.getOrganizationById(
          organizationId
      );

      return organization
          ? organization.name
          : this.$t('Organization not found');
    },

    formatDate(date) {
      if (!date) {
        return '-';
      }

      const parsedDate = new Date(date);

      if (Number.isNaN(parsedDate.getTime())) {
        return date;
      }

      return parsedDate.toLocaleDateString();
    },

    formatPrice(price) {
      const numericPrice = Number(price || 0);

      return `${numericPrice.toLocaleString('en-CH', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })} CHF`;
    },

    getStateClass(state) {
      const normalizedState = String(state || '').toLowerCase();

      if (['active', 'valid', 'enabled'].includes(normalizedState)) {
        return 'license-status--active';
      }

      if (['expired', 'disabled', 'invalid'].includes(normalizedState)) {
        return 'license-status--inactive';
      }

      if (['pending', 'scheduled'].includes(normalizedState)) {
        return 'license-status--pending';
      }

      return 'license-status--unknown';
    },

    getDateClass(date, isStartDate = false) {
      if (!date) {
        return 'date-grey';
      }

      const today = new Date();
      const targetDate = new Date(date);

      today.setHours(0, 0, 0, 0);
      targetDate.setHours(0, 0, 0, 0);

      if (Number.isNaN(targetDate.getTime())) {
        return 'date-grey';
      }

      if (isStartDate) {
        return targetDate > today
            ? 'date-grey'
            : 'date-green';
      }

      if (targetDate < today) {
        return 'date-red';
      }

      return 'date-green';
    }
  }
};
</script>

<style lang="scss" scoped>
.license-table {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-top: 24px;
}

.license-table__filters {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px;
  padding: 16px 18px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.03);
}

.license-table__filters {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 16px;
  padding: 18px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.03);
}

.filter-block {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-right: 16px;
  border-right: 1px solid rgba(255, 255, 255, 0.08);
}

.filter-block--small {
  min-width: 150px;
}

.filter-block--sort {
  min-width: 210px;
}

.filter-block__label {
  color: rgba(255, 255, 255, 0.65);
  font-size: 12px;
  font-weight: 700;
}

.date-range {
  display: flex;
  align-items: flex-end;
  gap: 8px;
}

.date-range__separator {
  padding-bottom: 8px;
  color: rgba(255, 255, 255, 0.35);
}

.filter-field {
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin: 0;

  > span:not(.filter-block__label) {
    color: rgba(255, 255, 255, 0.45);
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }
}

.filter-input {
  height: 36px;
  min-width: 130px;
  padding: 0 10px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.25);
  color: inherit;
  font: inherit;
  font-size: 13px;

  &:focus {
    border-color: rgba(242, 201, 76, 0.65);
    outline: none;
    box-shadow: 0 0 0 3px rgba(242, 201, 76, 0.08);
  }
}

.sort-control {
  display: flex;
  gap: 6px;

  .filter-input {
    flex: 1;
    min-width: 150px;
  }
}

.sort-direction {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 34px;
  flex-shrink: 0;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.07);
  color: #ece9ec;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    border-color: #c7590b;
    background: rgba(255, 67, 0, 0.1);
  }
}

.filter-reset {
  height: 36px;
  margin-left: auto;
  padding: 0 14px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  background: transparent;
  color: rgba(255, 255, 255, 0.65);
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    border-color: rgba(255, 255, 255, 0.3);
    background: rgba(255, 255, 255, 0.07);
    color: inherit;
  }
}

@media (max-width: 900px) {
  .license-table__filters {
    align-items: stretch;
  }

  .filter-block {
    width: calc(50% - 8px);
    padding-right: 0;
    border-right: none;
  }

  .filter-reset {
    margin-left: 0;
    align-self: flex-end;
  }
}

@media (max-width: 600px) {
  .filter-block {
    width: 100%;
  }

  .date-range {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
  }

  .filter-input {
    width: 100%;
    min-width: 0;
  }

  .filter-reset {
    width: 100%;
  }
}

.license-table__empty {
  padding: 40px 24px;
  border: 1px dashed rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  color: rgba(255, 255, 255, 0.55);
  text-align: center;
}

.license-table__toolbar {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 10px 14px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.03);
}

.select-all-label {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 9px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  user-select: none;
  margin-bottom: 0;
}

.selection-count {
  color: rgba(255, 255, 255, 0.55);
  font-weight: 400;
}

.pretty-checkbox {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
}

.pretty-checkbox__box {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  border: 1px solid rgba(255, 255, 255, 0.28);
  border-radius: 5px;
  background: rgba(0, 0, 0, 0.18);
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.18);
  transition:
      border-color 0.15s ease,
      background 0.15s ease,
      box-shadow 0.15s ease,
      transform 0.15s ease;
}

.pretty-checkbox__box::after {
  width: 5px;
  height: 9px;
  border-right: 2px solid #222222;
  border-bottom: 2px solid #222222;
  content: "";
  opacity: 0;
  transform: translateY(-1px) rotate(45deg) scale(0.7);
  transition:
      opacity 0.12s ease,
      transform 0.12s ease;
}

.pretty-checkbox:checked + .pretty-checkbox__box {
  border-color: #f2c94c;
  background: #f2c94c;
  box-shadow: 0 0 0 3px rgba(242, 201, 76, 0.12);
}

.pretty-checkbox:checked + .pretty-checkbox__box::after {
  opacity: 1;
  transform: translateY(-1px) rotate(45deg) scale(1);
}

.pretty-checkbox:indeterminate + .pretty-checkbox__box {
  border-color: #f2c94c;
  background: #f2c94c;
  box-shadow: 0 0 0 3px rgba(242, 201, 76, 0.12);
}

.pretty-checkbox:indeterminate + .pretty-checkbox__box::after {
  width: 9px;
  height: 2px;
  border: none;
  border-radius: 999px;
  background: #222222;
  opacity: 1;
  transform: none;
}

.pretty-checkbox:focus-visible + .pretty-checkbox__box {
  outline: none;
  border-color: rgba(255, 255, 255, 0.72);
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.12);
}

.select-all-label:hover .pretty-checkbox__box,
.license-row__select:hover .pretty-checkbox__box {
  border-color: rgba(255, 255, 255, 0.55);
  transform: translateY(-1px);
}

.license-row {
  display: flex;
  align-items: stretch;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.04);
  box-shadow: 0 5px 18px rgba(0, 0, 0, 0.14);
  transition:
      transform 0.15s ease,
      border-color 0.15s ease,
      background 0.15s ease;
}

.license-row:hover {
  transform: translateY(-1px);
  border-color: rgba(255, 255, 255, 0.22);
  background: rgba(255, 255, 255, 0.06);
}

.license-row--selected {
  border-color: rgba(242, 201, 76, 0.4);
  background: rgba(242, 201, 76, 0.05);
}

.license-row--selected:hover {
  border-color: rgba(242, 201, 76, 0.55);
  background: rgba(242, 201, 76, 0.08);
}

.license-row__select {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 46px;
  flex-shrink: 0;
  border-right: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(0, 0, 0, 0.08);
  cursor: pointer;
}

.license-row__main {
  flex: 1;
  min-width: 0;
  padding: 20px 22px;
}

.license-row__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 20px;
}

.license-row__title {
  font-size: 17px;
  font-weight: 600;
}

.license-row__subtitle {
  margin-top: 4px;
  color: rgba(255, 255, 255, 0.5);
  font-size: 12px;
}

.license-row__grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(100px, 1fr));
  gap: 18px;
}

.license-field {
  min-width: 0;
}

.license-field__label {
  margin-bottom: 6px;
  color: rgba(255, 255, 255, 0.48);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.license-field__value {
  overflow: hidden;
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
  font-weight: 500;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.license-type {
  display: inline-flex;
  padding: 4px 9px;
  border-radius: 5px;
  background: rgba(255, 255, 255, 0.09);
  font-size: 12px;
  text-transform: capitalize;
}

.license-status {
  flex-shrink: 0;
  padding: 5px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.license-status--active {
  background: rgba(71, 194, 125, 0.15);
  color: #62d492;
}

.license-status--inactive {
  background: rgba(235, 87, 87, 0.15);
  color: #ff7979;
}

.license-status--pending {
  background: rgba(242, 201, 76, 0.15);
  color: #f2c94c;
}

.license-status--unknown {
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.65);
}

.license-row__actions {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 9px;
  width: 120px;
  padding: 18px;
  border-left: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(0, 0, 0, 0.12);
}

.license-action {
  width: 100%;
  min-height: 36px;
  padding: 7px 12px;
  border: 1px solid transparent;
  border-radius: 6px;
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition:
      background 0.15s ease,
      border-color 0.15s ease,
      transform 0.15s ease;
}

.license-action:hover {
  transform: translateY(-1px);
}

.license-action--edit {
  border-color: rgba(255, 255, 255, 0.18);
  background: rgba(255, 255, 255, 0.08);
  color: inherit;
}

.license-action--edit:hover {
  background: rgba(255, 255, 255, 0.14);
}

.license-action--delete {
  border-color: rgba(235, 87, 87, 0.3);
  background: rgba(235, 87, 87, 0.09);
  color: #ff7979;
}

.license-action--delete:hover {
  background: rgba(235, 87, 87, 0.18);
}

.date-grey {
  color: rgba(255, 255, 255, 0.5);
}

.date-red {
  color: #ff7979;
}

.date-green {
  color: #62d492;
}

@media (max-width: 1200px) {
  .license-row__grid {
    grid-template-columns: repeat(3, minmax(120px, 1fr));
  }

  .filter-group {
    min-width: 120px;
    flex: 1 1 calc(33% - 12px);
  }
}

@media (max-width: 760px) {
  .license-row {
    flex-direction: column;
  }

  .license-row__select {
    width: auto;
    padding: 10px 14px;
    border-right: none;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    flex-direction: row;
    justify-content: flex-start;
  }

  .license-row__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .license-row__actions {
    flex-direction: row;
    width: auto;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    border-left: none;
  }
}

@media (max-width: 480px) {
  .license-row__main {
    padding: 18px;
  }

  .license-row__header {
    flex-direction: column;
    gap: 10px;
  }

  .license-row__grid {
    grid-template-columns: 1fr;
  }
}
</style>
